import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../utils/axios';
import { getImageUrl } from '../../config/apiConfig';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Eye, 
  EyeOff, 
  Search, 
  Filter, 
  DownloadCloud, 
  Upload,
  Image as ImageIcon,
  GripVertical,
  Save,
  X,
  Check,
  AlertCircle,
  RefreshCw,
  BarChart3,
  Palette,
  Link as LinkIcon,
  Type,
  FileText
} from 'lucide-react';

const AdminPromotionalSlides = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState(null);
  const [editingSlide, setEditingSlide] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    image_url: '',
    link: '',
    sort_order: 0,
    is_active: true
  });

  // Icon options - removed since we're image-only
  const iconOptions = [];

  // Background color options - kept for fallback
  const bgColorOptions = [
    { value: 'from-blue-600 to-blue-800', label: 'Blue' },
    { value: 'from-purple-600 to-purple-800', label: 'Purple' },
    { value: 'from-green-600 to-green-800', label: 'Green' },
    { value: 'from-red-600 to-red-800', label: 'Red' },
    { value: 'from-indigo-600 to-indigo-800', label: 'Indigo' },
    { value: 'from-cyan-600 to-cyan-800', label: 'Cyan' },
    { value: 'from-teal-600 to-teal-800', label: 'Teal' },
    { value: 'from-orange-600 to-orange-800', label: 'Orange' },
    { value: 'from-yellow-600 to-yellow-800', label: 'Yellow' },
    { value: 'from-pink-600 to-pink-800', label: 'Pink' },
    { value: 'from-violet-600 to-violet-800', label: 'Violet' },
    { value: 'from-emerald-600 to-emerald-800', label: 'Emerald' },
    { value: 'from-rose-600 to-rose-800', label: 'Rose' },
    { value: 'from-fuchsia-600 to-fuchsia-800', label: 'Fuchsia' },
    { value: 'from-amber-600 to-amber-800', label: 'Amber' }
  ];

  useEffect(() => {
    fetchSlides();
    fetchStats();
  }, []);

  const fetchSlides = async () => {
    try {
      setLoading(true);
      const response = await api.get('/promotional-slides/admin/all');
      setSlides(response.data.data);
    } catch (error) {
      setError('Failed to load promotional slides');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/promotional-slides/statistics/overview');
      setStats(response.data.data);
    } catch (error) {
      // Error handled silently
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(''); // Clear any previous errors
    
    try {
      // Form data prepared
      
      let response;
      
      if (editingSlide) {
        // For editing, send JSON data
      const slideData = {
        title: formData.title || 'Banner ' + Date.now(),
        subtitle: '', // Empty for image-only banners
        description: '', // Empty for image-only banners
        bg_color: 'from-blue-600 to-blue-800', // Default fallback
        icon_name: 'Image', // Default icon
        image_url: formData.image_url,
        link: formData.link || '',
        sort_order: formData.sort_order || 0,
        is_active: formData.is_active
      };
      
        response = await api.put(`/promotional-slides/${editingSlide.id}`, slideData);
      } else {
        // For creating new slide, check if we have a file to upload
        if (imageFile) {
          // File upload
          const formDataToSend = new FormData();
          formDataToSend.append('title', formData.title || 'Banner ' + Date.now());
          formDataToSend.append('image', imageFile);
          formDataToSend.append('link', formData.link || '');
          formDataToSend.append('sort_order', formData.sort_order || 0);
          formDataToSend.append('is_active', formData.is_active ? 1 : 0);
          
          response = await api.post('/promotional-slides', formDataToSend, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } else {
          // JSON data (for existing image URLs)
          const slideData = {
            title: formData.title || 'Banner ' + Date.now(),
            subtitle: '', // Empty for image-only banners
            description: '', // Empty for image-only banners
            bg_color: 'from-blue-600 to-blue-800', // Default fallback
            icon_name: 'Image', // Default icon
            image_url: formData.image_url,
            link: formData.link || '',
            sort_order: formData.sort_order || 0,
            is_active: formData.is_active
          };
          
        response = await api.post('/promotional-slides', slideData);
        }
      }
      
      setShowForm(false);
      setEditingSlide(null);
      resetForm();
      fetchSlides();
      fetchStats();
    } catch (error) {
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save banner';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (slide) => {
    setEditingSlide(slide);
    setFormData({
      title: slide.title,
      image_url: slide.image_url || '',
      link: slide.link,
      sort_order: slide.sort_order,
      is_active: slide.is_active
    });
    setImagePreview(getImageUrl(slide.image_url));
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this slide?')) return;
    
    try {
      await api.delete(`/promotional-slides/${id}`);
      fetchSlides();
      fetchStats();
    } catch (error) {
      setError('Failed to delete slide');
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await api.patch(`/promotional-slides/${id}/toggle`);
      fetchSlides();
      fetchStats();
    } catch (error) {
      setError('Failed to toggle slide status');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      image_url: '',
      link: '',
      sort_order: 0,
      is_active: true
    });
    setImagePreview(null);
    setImageFile(null);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      
      // Validate file size (max 5MB for file upload)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB. Please compress the image.');
        return;
      }
      
      // Store the file for upload
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData(prev => ({ ...prev, image_url: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB. Please compress the image.');
        return;
      }
      
      // Store the file for upload
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData(prev => ({ ...prev, image_url: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Image compression function
  const compressImage = (dataUrl, callback) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set maximum dimensions
      const maxWidth = 1200;
      const maxHeight = 600;
      
      let { width, height } = img;
      
      // Calculate new dimensions
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7); // 70% quality
      
      callback(compressedDataUrl);
    };
    img.src = dataUrl;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, image_url: '' }));
    setImageFile(null);
  };

  // Helper function to get proper image URL
  // Using getImageUrl from apiConfig

  const filteredSlides = slides.filter(slide =>
    slide.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    slide.subtitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
    slide.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-soft border border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{title}</p>
          <p className="text-lg font-bold text-gray-900 dark:text-white">{value}</p>
        </div>
        <div className={`p-2 rounded-lg ${color} bg-opacity-10`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 gap-3 mt-1">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-1">Promotional Slides</h1>
          <p className="text-xs text-gray-600 dark:text-gray-400">Manage hero section promotional content and images</p>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => { setShowForm(true); setEditingSlide(null); resetForm(); }}
            className="btn btn-primary-gradient btn-sm flex items-center px-3 py-2 text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Slide
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 px-3 py-2 rounded-lg text-center text-xs border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800 mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin mb-3 text-primary-600" />
          <p className="text-sm text-gray-700 dark:text-gray-300">Loading promotional slides...</p>
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
              <StatCard
                title="Total Slides"
                value={stats.total}
                icon={BarChart3}
                color="bg-blue-500"
              />
              <StatCard
                title="Active"
                value={stats.active}
                icon={Eye}
                color="bg-green-500"
              />
              <StatCard
                title="Inactive"
                value={stats.inactive}
                icon={EyeOff}
                color="bg-yellow-500"
              />
            </div>
          )}

          {/* Filters and Search */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-soft border border-gray-100 dark:border-gray-700 mb-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div className="flex flex-col sm:flex-row gap-3 flex-1">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search slides..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="btn btn-outline btn-sm flex items-center text-xs"
                >
                  <Filter className="w-3 h-3 mr-1" />
                  Filters
                </button>
              </div>
            </div>
          </div>

          {/* Slides Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSlides.map((slide) => (
              <div key={slide.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-soft border border-gray-100 dark:border-gray-700 overflow-hidden">
                {/* Slide Preview */}
                <div className="h-48 relative">
                  {slide.image_url ? (
                    <img 
                      src={getImageUrl(slide.image_url)} 
                      alt={slide.title}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        console.error('❌ Admin panel image failed to load:', getImageUrl(slide.image_url));
                        // Safely handle the error without accessing potentially invalid DOM nodes
                        if (e && e.target && e.target.style) {
                          e.target.style.display = 'none';
                        }
                      }}
                    />
                  ) : (
                    <div className={`w-full h-full bg-gradient-to-r ${slide.bg_color} flex items-center justify-center`}>
                      <ImageIcon className="w-12 h-12 text-white/50" />
                    </div>
                  )}
                  <div className="absolute top-2 right-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      slide.is_active 
                        ? 'bg-green-500/20 text-green-200' 
                        : 'bg-gray-500/20 text-gray-200'
                    }`}>
                      {slide.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                {/* Slide Content */}
                <div className="p-3">
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-1 truncate">
                    {slide.title || 'Untitled Banner'}
                  </h3>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-3">
                    <span>Order: {slide.sort_order}</span>
                    <span>{new Date(slide.created_at).toLocaleDateString()}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleEdit(slide)}
                      className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                      title="Edit"
                    >
                      <Edit3 className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleToggleStatus(slide.id)}
                      className={`p-1 rounded transition-colors ${
                        slide.is_active 
                          ? 'text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20' 
                          : 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                      }`}
                      title={slide.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {slide.is_active ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    </button>
                    <button
                      onClick={() => handleDelete(slide.id)}
                      className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredSlides.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-500 dark:text-gray-400">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-sm">No promotional slides found</p>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
            onClick={() => { setShowForm(false); setEditingSlide(null); resetForm(); }}
          />
          {/* Modal */}
          <div className="relative bg-white/90 dark:bg-gray-900/90 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[75vh] overflow-y-auto p-0 animate-fadeInScale mt-16">
            {/* Close Button */}
            <button
              onClick={() => { setShowForm(false); setEditingSlide(null); resetForm(); }}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 dark:bg-gray-800/80 shadow hover:bg-red-500 hover:text-white transition"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {editingSlide ? 'Edit Slide' : 'Add New Slide'}
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Title (for admin reference only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Banner Name (Admin Reference)
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                    placeholder="e.g., Summer Sale Banner"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    This is only for admin reference and won't be displayed on the website
                  </p>
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Banner Image *
                  </label>
                  <div 
                    className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
                    onDrop={handleImageDrop}
                    onDragOver={handleDragOver}
                  >
                    {imagePreview ? (
                      <div className="space-y-4">
                        <div className="relative inline-block">
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            className="w-48 h-32 object-contain rounded-lg border shadow-lg bg-gray-100 dark:bg-gray-800"
                          />
                          <button
                            type="button"
                            onClick={removeImage}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                            title="Remove image"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Image uploaded successfully
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-gray-400" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            Drag and drop an image here, or click to select
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500">
                            PNG, JPG, GIF up to 2MB (will be compressed automatically)
                          </p>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                          id="image-upload"
                        />
                        <label
                          htmlFor="image-upload"
                          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 cursor-pointer"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Choose Image
                        </label>
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Recommended size: 1200x600px. Images will be automatically compressed to optimize performance.
                  </p>
                </div>

                {/* Link (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Link (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.link}
                    onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                    placeholder="/courses/example or https://example.com"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    When clicked, the banner will navigate to this link
                  </p>
                </div>

                {/* Sort Order */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                    min="0"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Lower numbers appear first
                  </p>
                </div>

                {/* Active Status */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Active (visible on website)
                  </label>
                </div>

                {/* Form Actions */}
                <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setEditingSlide(null); resetForm(); }}
                    className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn btn-primary-gradient btn-sm flex items-center px-4 py-2 text-sm"
                  >
                    {saving ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {editingSlide ? 'Update Banner' : 'Create Banner'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminPromotionalSlides; 